/* ============================================
   ChatSearchBar — Search messages within
   current conversation or across all conversations
   ============================================ */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { IconSearch, IconArrowDown, IconArrowUp, IconXCircle, IconCalendar } from '@/components/icons/Icons'
import type { Mensaje, Contacto } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface SearchResult extends Mensaje {
  conversacion?: {
    id: number
    chat_id: string
    canal: string
    contacto_id: number | null
    channel_id: string | null
  }
}

interface ChatSearchBarProps {
  conversacionId: number
  contactMap?: Record<number, Contacto>
  onScrollToMessage: (messageId: number) => void
  onJumpToDate: (isoDate: string) => void | Promise<void>
  onClose: () => void
}

function highlightMatch(text: string, query: string): string {
  if (!query.trim() || !text) return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${escaped})`, 'gi')
  return text.replace(re, '<mark class="chat-search-highlight">$1</mark>')
}

/** Hoy en formato YYYY-MM-DD (hora local) — tope del selector de fecha */
function todayIso(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function formatSearchDate(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.floor((today.getTime() - msgDate.getTime()) / 86400000)

  if (diffDays === 0) return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 1) return 'Ayer'
  if (diffDays < 7) return d.toLocaleDateString('es-PE', { weekday: 'short' })
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

const CANAL_INTERNAL_TO_URL: Record<string, string> = {
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  web_chat: 'webchat',
  email: 'email',
  sms: 'sms',
  portal: 'portal',
}

export default function ChatSearchBar({
  conversacionId,
  contactMap = {},
  onScrollToMessage,
  onJumpToDate,
  onClose,
}: ChatSearchBarProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [scope, setScope] = useState<'current' | 'all'>('current')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [hasSearched, setHasSearched] = useState(false)
  const [jumpDate, setJumpDate] = useState('')
  const [jumping, setJumping] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Search function
  const performSearch = useCallback(async (q: string) => {
    setLoading(true)
    setActiveIndex(-1)
    try {
      const token = localStorage.getItem('auth_token')
      const params = new URLSearchParams({ q, limit: '30' })
      if (scope === 'current') {
        params.set('conversacion_id', String(conversacionId))
      }

      const res = await fetch(`${API_URL}/api/chat/mensajes/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setResults(data.results || [])
      } else {
        setResults([])
      }
    } catch {
      setResults([])
    } finally {
      setLoading(false)
      setHasSearched(true)
    }
  }, [scope, conversacionId])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query.trim() || query.trim().length < 2) {
      setResults([])
      setHasSearched(false)
      setActiveIndex(-1)
      return
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query.trim())
    }, 350)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, scope, performSearch])

  const handleResultClick = (result: SearchResult) => {
    if (scope === 'current' || result.conversacion_id === conversacionId) {
      // Same conversation — scroll to message, close after delay
      onScrollToMessage(result.id)
      // Close search after scroll animation completes
      setTimeout(() => onClose(), 600)
    } else if (result.conversacion) {
      // Different conversation — navigate and close
      const conv = result.conversacion
      const canalSlug = CANAL_INTERNAL_TO_URL[conv.canal] || conv.canal
      const origen = conv.channel_id || 'default'
      onClose()
      navigate(`/conversaciones/${canalSlug}/${origen}/${conv.id}`)
    }
  }

  // Jump to a calendar date within the current conversation.
  // El salto NO se dispara en onChange: el calendario nativo emite change
  // apenas la fecha queda completa y cerrar/deshabilitar el input en ese
  // momento corta la navegacion entre meses. Se ejecuta con el boton o Enter.
  const runJumpDate = async () => {
    if (!jumpDate || jumping) return
    setJumping(true)
    try {
      await onJumpToDate(jumpDate)
    } finally {
      setJumping(false)
    }
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => Math.max(prev - 1, 0))
    }
    if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) {
      e.preventDefault()
      handleResultClick(results[activeIndex])
    }
  }

  // Scroll active result into view
  useEffect(() => {
    if (activeIndex >= 0 && resultsRef.current) {
      const el = resultsRef.current.children[activeIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  // Navigate between results in current conversation
  const navigateResult = (direction: 'up' | 'down') => {
    if (results.length === 0) return
    const newIdx = direction === 'down'
      ? Math.min(activeIndex + 1, results.length - 1)
      : Math.max(activeIndex - 1, 0)
    setActiveIndex(newIdx)
    if (scope === 'current' && results[newIdx]) {
      onScrollToMessage(results[newIdx].id)
    }
  }

  function resolveContactName(result: SearchResult): string {
    if (result.conversacion?.contacto_id) {
      const c = contactMap[result.conversacion.contacto_id]
      if (c) return `${c.nombre}${c.apellido ? ' ' + c.apellido : ''}`
    }
    return result.conversacion?.chat_id || ''
  }

  return (
    <div className="chat-search-bar">
      <div className="chat-search-bar__row">
        <div className="chat-search-bar__input-wrap">
          <IconSearch size={16} />
          <input
            ref={inputRef}
            type="text"
            placeholder={scope === 'current' ? 'Buscar en esta conversacion...' : 'Buscar en todas las conversaciones...'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {loading && <div className="auth-spinner" style={{ width: 14, height: 14 }} />}
        </div>

        {/* Jump to date (solo tiene sentido dentro de la conversacion actual) */}
        {scope === 'current' && (
          <div className="chat-search-bar__date">
            <IconCalendar size={15} />
            <input
              type="date"
              value={jumpDate}
              max={todayIso()}
              title="Ir a una fecha"
              onChange={(e) => setJumpDate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  runJumpDate()
                }
                if (e.key === 'Escape') onClose()
              }}
            />
            <button
              className="chat-search-date-go"
              onClick={runJumpDate}
              disabled={!jumpDate || jumping}
              type="button"
              title="Ir a esa fecha"
            >
              {jumping ? <div className="auth-spinner" /> : 'Ir'}
            </button>
          </div>
        )}

        {/* Scope toggle */}
        <div className="chat-search-bar__scope">
          <button
            className={`chat-search-scope-btn ${scope === 'current' ? 'chat-search-scope-btn--active' : ''}`}
            onClick={() => setScope('current')}
            type="button"
          >
            Esta
          </button>
          <button
            className={`chat-search-scope-btn ${scope === 'all' ? 'chat-search-scope-btn--active' : ''}`}
            onClick={() => setScope('all')}
            type="button"
          >
            Todas
          </button>
        </div>

        {/* Result navigation (current conv mode) */}
        {scope === 'current' && results.length > 0 && (
          <div className="chat-search-bar__nav">
            <span className="chat-search-bar__count">
              {activeIndex >= 0 ? activeIndex + 1 : 0}/{results.length}
            </span>
            <button
              className="chat-search-nav-btn"
              onClick={() => navigateResult('up')}
              disabled={activeIndex <= 0}
              type="button"
              title="Anterior"
            >
              <IconArrowUp size={14} />
            </button>
            <button
              className="chat-search-nav-btn"
              onClick={() => navigateResult('down')}
              disabled={activeIndex >= results.length - 1}
              type="button"
              title="Siguiente"
            >
              <IconArrowDown size={14} />
            </button>
          </div>
        )}

        <button
          className="chat-search-bar__close"
          onClick={onClose}
          type="button"
          title="Cerrar busqueda"
        >
          <IconXCircle size={18} />
        </button>
      </div>

      {/* Results dropdown — only show for global search or when there are results */}
      {hasSearched && query.trim().length >= 2 && (scope === 'all' || results.length > 0) && (
        <div className="chat-search-results" ref={resultsRef}>
          {results.length === 0 ? (
            <div className="chat-search-results__empty">
              No se encontraron resultados para "{query}"
            </div>
          ) : (
            results.map((result, idx) => (
              <button
                key={result.id}
                className={`chat-search-result ${idx === activeIndex ? 'chat-search-result--active' : ''}`}
                onClick={() => handleResultClick(result)}
                type="button"
              >
                <div className="chat-search-result__header">
                  {scope === 'all' && (
                    <span className="chat-search-result__contact">
                      {resolveContactName(result)}
                    </span>
                  )}
                  <span className="chat-search-result__date">
                    {formatSearchDate(result.fecha_mensaje)}
                  </span>
                </div>
                <div
                  className="chat-search-result__preview"
                  dangerouslySetInnerHTML={{
                    __html: highlightMatch(
                      (result.contenido || '').slice(0, 120),
                      query
                    ),
                  }}
                />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
